/**
 * The one place that decides what a published URL looks like.
 *
 * Before this module the site answered that question three times and got three
 * answers: ``scripts/generate-sitemap.mjs`` and ``scripts/generate-static-meta.mjs``
 * both composed a slashless URL, ``src/components/LanguageRouter.tsx`` published
 * ``BASE_URL + location.pathname`` — which carries whatever slash the visitor's
 * URL had — and Cloudflare served a fourth thing, redirecting the slashless form
 * to a trailing slash. Every declared URL and every internal link cost a crawler
 * a redirect hop. That is SEO backlog item ``B3``; this module is its fix.
 *
 * **The canonical form is slashless**: ``https://shelpuk.com/<lang>[/<route>]``.
 * The form is not enforced by the canonical tag — it is enforced by the shape of
 * the file Cloudflare is asked to serve. Cloudflare Pages applies
 * ``auto-trailing-slash`` HTML handling, under which a folder index
 * (``en/insights/index.html``) is canonically served *with* a slash and a flat
 * file (``en/insights.html``) *without* one. :func:`toOutputFilePath` is
 * therefore as load-bearing as :func:`toCanonicalUrl`: the two must agree, or
 * the site publishes one URL and serves another.
 *
 * ``.system_design/SYSTEM_DESIGN.md`` §11.3 carries the decision, the rejected
 * trailing-slash alternative and the cost accepted for choosing this one.
 *
 * **This module is imported by the two ``.mjs`` build generators as well as by
 * the application**, through their ad-hoc TypeScript loader, which rewrites
 * *relative* import specifiers only. It must therefore stay dependency-free —
 * an ``@/``-aliased import added here would break ``npm run build`` while Vite
 * and Vitest stayed green. The same constraint governs
 * :mod:`src/lib/routeInventory`.
 */

/**
 * The site's origin, with no trailing slash.
 *
 * Declared here rather than in each consumer because a URL is an origin and a
 * path, and this module owns the pair. Three copies of this literal existed
 * before it.
 */
export const SITE_ORIGIN = "https://shelpuk.com";

/**
 * Reduce a URL path to the site's canonical slashless form.
 *
 * Collapses repeated separators, supplies a leading slash, and removes trailing
 * slashes. The site root is the one path that keeps its slash, because a URL
 * path is never empty.
 *
 * The function is **idempotent** — ``normalizeUrlPath(normalizeUrlPath(p))``
 * equals ``normalizeUrlPath(p)`` for every input. That is what lets the
 * generators, the runtime and the tests each apply it without knowing whether
 * another layer already did.
 *
 * Case and percent-encoding are preserved: both are significant in a URL path,
 * and decoding here would silently change which resource the URL names.
 *
 * Args:
 *     pathname: A URL path, with or without a leading slash. Query strings and
 *         fragments are the caller's to strip; they are never passed in.
 *
 * Returns:
 *     The canonical path, beginning with a slash and — except for the root —
 *     ending in a path segment.
 */
export function normalizeUrlPath(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);
  return segments.length === 0 ? "/" : `/${segments.join("/")}`;
}

/**
 * Build the absolute canonical URL for a path.
 *
 * Args:
 *     pathname: A URL path in any form :func:`normalizeUrlPath` accepts.
 *
 * Returns:
 *     The absolute URL a page should declare as its canonical, and the same
 *     string the sitemap declares as its ``<loc>``.
 */
export function toCanonicalUrl(pathname: string): string {
  const normalized = normalizeUrlPath(pathname);

  // `https://shelpuk.com` and `https://shelpuk.com/` are equivalent per RFC 3986
  // §6.2.3, and the bare form is what `public/llms.txt` and the root document
  // already publish. Step E3.3 owns whether the root is published at all.
  return normalized === "/" ? SITE_ORIGIN : `${SITE_ORIGIN}${normalized}`;
}

/**
 * What a URL path's first segment says about its locale prefix.
 *
 * ``LanguageRouter`` used to answer this question twice — the rescue effect
 * stripped an unsupported two-letter prefix, ``useLanguageNavigation``'s
 * ``changeLanguage`` prepended over it and produced a 404 route — and the two
 * agreed only by accident of render order (CWS-62). This classification is the
 * one answer: both call sites compose from ``pathWithoutPrefix`` and have
 * nothing left to compute, so the interpretations cannot drift apart again.
 * ``SYSTEM_DESIGN.md`` §9.2 carries the asymmetry this resolves.
 *
 * The two-letter test is case-insensitive while the supported list is not, so
 * ``/PT/x`` and ``/EN/x`` are both "unsupported" — case-normalisation is
 * i18next's job, not this function's.
 */
export interface ClassifiedUrlLanguagePrefix {
  /**
   * ``"supported"`` when the first segment is one of ``supportedLanguages``,
   * ``"unsupported"`` when it is a two-letter code that is not, and
   * ``"absent"`` when there is no first segment or it is not two letters.
   */
  kind: "supported" | "unsupported" | "absent";
  /** The locale exactly as the URL carries it, or ``null`` when absent. */
  language: string | null;
  /**
   * The path with its first segment removed — a leading slash when anything
   * remains, ``""`` when nothing does — so
   * ``/${targetLanguage}${pathWithoutPrefix}`` is always a usable path.
   */
  pathWithoutPrefix: string;
}

/**
 * Classify a URL path's first segment against a supported-language list.
 *
 * Args:
 *     pathname: A URL path; query strings and fragments are the caller's to
 *         keep, and are not part of the classification.
 *     supportedLanguages: The supported locale codes, exact-case. A parameter
 *         rather than an import so this module stays dependency-free for the
 *         build generators, and the contract stays testable against fixtures.
 *
 * Returns:
 *     The classification, carrying the stripped remainder for composition.
 */
export function classifyUrlLanguagePrefix(
  pathname: string,
  supportedLanguages: readonly string[],
): ClassifiedUrlLanguagePrefix {
  const segments = pathname.split("/").filter(Boolean);
  const [first, ...rest] = segments;

  const kind =
    first === undefined
      ? ("absent" as const)
      : supportedLanguages.includes(first)
        ? ("supported" as const)
        : /^[a-z]{2}$/i.test(first)
          ? ("unsupported" as const)
          : ("absent" as const);

  // A supported or unsupported prefix strips exactly its one segment; "absent"
  // strips nothing, because prepending the target over the whole path is what
  // keeps `/services/consulting` from losing a real segment.
  const remainder = kind === "absent" ? segments : rest;
  const pathWithoutPrefix = remainder.length > 0 ? `/${remainder.join("/")}` : "";

  return {
    kind,
    language: kind === "absent" ? null : (first as string),
    pathWithoutPrefix,
  };
}

/**
 * Map a URL path to the build-output file Cloudflare serves at it with HTTP 200.
 *
 * A flat ``<route>.html`` rather than ``<route>/index.html``: under Cloudflare's
 * ``auto-trailing-slash`` handling that is precisely the difference between the
 * slashless URL returning 200 and returning a 308 to the trailing-slash form.
 *
 * Args:
 *     pathname: A URL path in any form :func:`normalizeUrlPath` accepts.
 *
 * Returns:
 *     A relative, POSIX-separated path beneath the build output directory.
 *
 * Raises:
 *     Error: When a segment is ``.`` or ``..``. The return value is joined onto
 *         ``dist/`` and written to, so a traversal segment is the one input
 *         class that must fail loudly rather than be normalised away.
 */
export function toOutputFilePath(pathname: string): string {
  const segments = normalizeUrlPath(pathname).split("/").filter(Boolean);

  if (segments.some((segment) => segment === "." || segment === "..")) {
    throw new Error(
      `Refusing to build an output path from "${pathname}": a "." or ".." segment ` +
        `would escape the build output directory.`,
    );
  }

  return segments.length === 0 ? "index.html" : `${segments.join("/")}.html`;
}
